import { Link } from "@tanstack/react-router";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  Siren,
  ArrowUpRight,
  Clock,
} from "lucide-react";
import { LocationMap } from "./LocationMap";
import { useContactSettings, type SocialLink } from "@/hooks/useContactSettings";
import { useHomeSettings } from "@/hooks/useHomeSettings";
import knmcLogo from "@/assets/knmc-logo.png";

type FooterLink = { label: string; to: string };

const patients: FooterLink[] = [
  { label: "Book Appointment", to: "/doctors" },
  { label: "Find a Doctor", to: "/doctors" },
  { label: "Health Packages", to: "/health-packages" },
];

const hospital: FooterLink[] = [
  { label: "About KNMC", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Departments", to: "/departments" },
  { label: "Health Articles", to: "/health-articles" },
];

const iconByPlatform: Record<string, typeof Facebook> = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

function resolveSocialIcon(platform: string) {
  return iconByPlatform[platform.toLowerCase()] || Globe;
}

function phoneHref(phone: string) {
  const clean = phone.replace(/[\s\-\(\)]+/g, "");
  return `tel:${clean}`;
}

export function Footer() {
  const { settings } = useContactSettings();
  const { settings: homeSettings } = useHomeSettings();
  const brandName = homeSettings?.header?.brand_name || "Kathmandu National";
  const brandSubtitle = homeSettings?.header?.brand_subtitle || "Medical College & Teaching Hospital";
  const s = settings;
  const ep = s?.emergency_phone || "";
  const cp = s?.phone || "";

  const directionsUrl = s?.map_direction_url || "https://maps.app.goo.gl/HVUdGdVyUJmKZK4W9";

  return (
    <footer className="relative overflow-hidden bg-primary text-primary-foreground">
      {/* Decorative gold hairlines */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gold/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-secondary/20 blur-3xl"
      />

      {/* Emergency Section - Distinct Card with Top & Bottom Spacing */}
      <div className="relative mx-auto max-w-7xl px-6 pt-12 pb-6 md:pt-16 md:pb-8">
        <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-black/30 p-6 md:p-8 shadow-2xl backdrop-blur-md">
          {/* Ambient red emergency glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emergency/20 blur-2xl"
          />

          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emergency text-white shadow-md">
                <Siren className="h-5 w-5" />
                <span className="absolute inset-0 animate-ping rounded-full bg-emergency/40" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold">
                  24 / 7 Emergency
                </p>
                <p className="mt-0.5 text-sm font-medium text-primary-foreground/90">
                  Trauma, cardiac & critical care — always open.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <a
                href={phoneHref(ep)}
                className="rounded-full bg-emergency px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
              >
                Call {ep} — Ambulance
              </a>
              {cp && (
                <a
                  href={phoneHref(cp)}
                  className="hidden rounded-full border border-white/20 bg-white/5 px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:border-gold hover:text-gold sm:inline-flex"
                >
                  {cp.split("\n")[0]}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links & Map */}
      <div className="relative mx-auto max-w-7xl px-6 pt-6 pb-16 md:pt-8">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-foreground p-1.5 shadow-soft ring-1 ring-gold/40">
                <img src={knmcLogo} alt="KNMC crest" className="h-full w-full object-contain" />
              </div>
              <div>
                <div className="font-display text-xl font-semibold leading-none">{brandName}</div>
                <div className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-primary-foreground/70">
                  {brandSubtitle}
                </div>
              </div>
            </Link>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-primary-foreground/75">
              A teaching hospital delivering compassionate, evidence-based care
              and shaping the next generation of medical professionals in Nepal.
            </p>

            <div className="mt-8 space-y-3.5 text-sm">
              {s?.address?.formatted && (
                <div className="flex items-start gap-3 text-primary-foreground/75">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>
                    {s.address.formatted.split("\n").map((line, i) => (
                      <span key={i}>{i > 0 && <br />}{line}</span>
                    ))}
                  </span>
                </div>
              )}
              {cp && (
                <a href={phoneHref(cp)} className="flex items-center gap-3 text-primary-foreground/85 hover:text-gold">
                  <Phone className="h-4 w-4 text-gold" />
                  <span>{cp.split("\n")[0]}</span>
                </a>
              )}
              {s?.email_id && (
                <a href={`mailto:${s.email_id}`} className="flex items-center gap-3 text-primary-foreground/85 hover:text-gold">
                  <Mail className="h-4 w-4 text-gold" />
                  <span>{s.email_id}</span>
                </a>
              )}
              {s?.office_hours && (
                <div className="flex items-start gap-3 text-primary-foreground/75">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>
                    {s.office_hours.split("\n").map((line, i) => (
                      <span key={i}>{i > 0 && <br />}{line}</span>
                    ))}
                  </span>
                </div>
              )}
            </div>

            {(s?.social_links?.length ?? 0) > 0 && (
              <div className="mt-7 flex gap-2.5">
                {s!.social_links.filter((l) => l.enabled).map((link: SocialLink) => {
                  const Icon = resolveSocialIcon(link.platform);
                  return (
                    <a
                      key={link.label || link.platform}
                      href={link.url || "#"}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={link.label || link.platform}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] transition-all hover:-translate-y-0.5 hover:border-gold hover:bg-gold hover:text-primary"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Link Columns */}
          <div className="grid grid-cols-2 gap-8 lg:col-span-3">
            <div>
              <h4 className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-gold">
                Patients
              </h4>
              <ul className="mt-5 space-y-3">
                {patients.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="group inline-flex items-center gap-1 text-sm text-primary-foreground/75 transition-colors hover:text-gold"
                    >
                      <span>{l.label}</span>
                      <ArrowUpRight className="h-3 w-3 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-gold">
                Hospital
              </h4>
              <ul className="mt-5 space-y-3">
                {hospital.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="group inline-flex items-center gap-1 text-sm text-primary-foreground/75 transition-colors hover:text-gold"
                    >
                      <span>{l.label}</span>
                      <ArrowUpRight className="h-3 w-3 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Map Column */}
          <div className="lg:col-span-5">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <h4 className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-gold">
                  Find Us
                </h4>
                <p className="mt-2 font-display text-lg text-primary-foreground">
                  Visit our Kathmandu campus
                </p>
              </div>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-gold/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-primary"
              >
                Directions
                <ArrowUpRight className="h-3 w-3" />
              </a>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-gold/40 via-secondary/30 to-primary/0 opacity-60 blur" />
              <LocationMap
                height={300}
                className="relative !rounded-2xl border-white/10"
                src={s?.map_embed_url || undefined}
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-primary-foreground/55">
          <div>
            © {new Date().getFullYear()} {brandName} {brandSubtitle}. All rights reserved.
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span>Designed and Powered with</span>
            <img
              src="https://opterp.com/files/opterp-logo.svg"
              alt="OptERP logo"
              style={{ height: "12px", verticalAlign: "middle" }}
            />
            <a
              href="https://opterp.com/"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-gold transition-colors hover:underline"
            >
              OptERP
            </a>
            <span>by</span>
            <img
              src="https://geofinity.com.np/_next/image?url=%2Fassets%2Flogo.webp&w=48&q=75"
              alt="OptERP logo"
              style={{ height: "12px", verticalAlign: "middle" }}
            />
            <a
              href="https://geofinity.com.np/"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-gold transition-colors hover:underline"
            >
              Geofinity Solutions Pvt. Ltd.
            </a>
          </div>

          <div className="flex gap-6">
            <a href="#" className="transition-colors hover:text-gold">Privacy</a>
            <a href="#" className="transition-colors hover:text-gold">Terms</a>
            <a href="#" className="transition-colors hover:text-gold">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
}